import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Box, 
  Container,
  Grid,
  TextField,
  Snackbar,
  Alert,
  Fade
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SendIcon from '@mui/icons-material/Send';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import { SectionContainer, PageTitle, FeatureCard } from '../Components/StyledComponents';
import Seo from "../Components/Seo";
import {
  BUSINESS_ADDRESS,
  BUSINESS_EMAIL,
  BUSINESS_PHONE,
  FACEBOOK_URL,
  GOOGLE_REVIEWS_URL,
  INSTAGRAM_URL,
} from "../config/business";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create WhatsApp message
    const message = `Hallo, mijn naam is ${formData.name}.%0A%0A${formData.message}%0A%0AContactgegevens:%0AEmail: ${formData.email}%0ATelefoon: ${formData.phone}`;
    const whatsappUrl = `https://wa.me/31681677899?text=${message}`;
    
    // Open WhatsApp
    window.open(whatsappUrl, '_blank');
    
    // Reset form and show confirmation
    setFormData({ name: '', email: '', phone: '', message: '' });
    setSnackbar({ 
      open: true, 
      message: 'WhatsApp wordt geopend met uw bericht!', 
      severity: 'success' 
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const contactInfo = [
    {
      icon: <PhoneIcon sx={{ fontSize: 40, color: 'secondary.main' }} />,
      title: 'Telefoon',
      content: BUSINESS_PHONE,
      action: { label: 'Bel Nu', href: 'tel:+31681677899' }
    },
    {
      icon: <WhatsAppIcon sx={{ fontSize: 40, color: 'secondary.main' }} />,
      title: 'WhatsApp',
      content: BUSINESS_PHONE,
      action: { label: 'WhatsApp', href: 'https://wa.me/31681677899' }
    },
    {
      icon: <EmailIcon sx={{ fontSize: 40, color: 'secondary.main' }} />,
      title: 'Email',
      content: BUSINESS_EMAIL,
      action: { label: 'Email Sturen', href: 'mailto:info@meesterstoffeerders.nl' }
    },
    {
      icon: <LocationOnIcon sx={{ fontSize: 40, color: 'secondary.main' }} />,
      title: 'Adres',
      content: BUSINESS_ADDRESS,
      action: { label: 'Route', href: 'https://www.google.com/maps/search/?api=1&query=Argonweg+66+Almere' }
    },
    {
      icon: <InstagramIcon sx={{ fontSize: 40, color: 'secondary.main' }} />,
      title: 'Instagram',
      content: '@meesterstoffeerders',
      action: { label: 'Bekijk Instagram', href: INSTAGRAM_URL }
    },
    {
      icon: <FacebookIcon sx={{ fontSize: 40, color: 'secondary.main' }} />,
      title: 'Facebook',
      content: 'Meester Stoffeerders',
      action: { label: 'Bekijk Facebook', href: FACEBOOK_URL }
    },
    {
      icon: <StarOutlineIcon sx={{ fontSize: 40, color: 'secondary.main' }} />,
      title: 'Google Reviews',
      content: 'Bekijk onze reviews op Google',
      action: { label: 'Lees reviews', href: GOOGLE_REVIEWS_URL }
    },
  ];

  return (
    <Fade in={true} timeout={800}>
      <Box>
        <Seo
          title="Contact | Meester Stoffeerders Almere"
          description="Neem contact op met Meester Stoffeerders in Almere voor meubelstoffering, restauratie, maatwerk en vrijblijvend advies."
          path="/contact"
        />
        <SectionContainer>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <PageTitle>Contact</PageTitle>
              <Typography 
                variant="h6" 
                color="text.secondary" 
                sx={{ mt: 3, maxWidth: '700px', mx: 'auto' }}
              >
                Neem contact met ons op voor een vrijblijvend adviesgesprek
              </Typography>
            </Box>

            {/* Contact Info Cards */}
            <Grid container spacing={3} sx={{ mb: 6 }}>
              {contactInfo.map((info, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <FeatureCard>
                    {info.icon}
                    <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                      {info.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {info.content}
                    </Typography>
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      href={info.action.href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {info.action.label}
                    </Button>
                  </FeatureCard>
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={4}>
              {/* Contact Form */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%', boxShadow: 3 }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
                      Stuur een Bericht
                    </Typography>
                    <Box component="form" onSubmit={handleSubmit}>
                      <TextField
                        fullWidth
                        label="Naam"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        margin="normal"
                        variant="outlined"
                      />
                      <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        margin="normal"
                        variant="outlined"
                      />
                      <TextField
                        fullWidth
                        label="Telefoon"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        margin="normal"
                        variant="outlined"
                      />
                      <TextField
                        fullWidth
                        label="Bericht"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        multiline
                        rows={4}
                        margin="normal"
                        variant="outlined"
                      />
                      <Button
                        type="submit"
                        variant="contained"
                        color="secondary"
                        size="large"
                        fullWidth
                        startIcon={<SendIcon />}
                        sx={{ mt: 2 }}
                      >
                        Verstuur via WhatsApp
                      </Button>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                        U wordt doorgestuurd naar WhatsApp
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Map */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%', boxShadow: 3 }}>
                  <CardContent sx={{ p: 0, height: '100%', '&:last-child': { pb: 0 } }}>
                    <iframe
                      title="Google Maps"
                      width="100%"
                      height="100%"
                      style={{ border: 0, minHeight: '500px', borderRadius: '12px' }}
                      src="https://www.google.com/maps/embed/v1/place?key=AIzaSyD5cvpT0nP6t070S36cCty8KKhtG6ddeSI&q=Argonweg+66+Almere&zoom=15"
                      allowFullScreen
                      loading="lazy"
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Business Hours */}
            <Box sx={{ mt: 6, textAlign: 'center', p: 4, backgroundColor: 'background.paper', borderRadius: 3 }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
                Openingstijden
              </Typography>
              <Grid container spacing={2} justifyContent="center">
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Maandag - Vrijdag
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    8:00 - 17:00
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Zaterdag
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Op afspraak
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Zondag
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Gesloten
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Container>
        </SectionContainer>

        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={6000} 
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Fade>
  );
};

export default Contact;
